import { inject, Injectable, NgZone } from '@angular/core';
import { fromEvent, merge, Subscription, timer } from 'rxjs';
import { startWith, switchMap } from 'rxjs/operators';
import { StorageService } from '../storage/storage.service';
import { STORAGE_KEYS } from '../../config/storage-keys.config';
import { environment } from '../../../../environments/environment';
import { AlertService } from '../alert/alert.service';
import { AuthService } from '../auth/auth.service';
import Swal from 'sweetalert2';


@Injectable({
  providedIn: 'root'
})
export class InactivityService {
  private authService = inject(AuthService);
  private storageService = inject(StorageService);
  private alertService = inject(AlertService);
  private ngZone = inject(NgZone);

  
  private inactivitySubscription?: Subscription;
  private warningTimerSubscription?: Subscription;
  /** Only the session-timeout dialog should be dismissed on user activity — not other Swal modals. */
  private inactivityWarningOpen = false;

  // Configuration from environment
  private readonly TOTAL_TIMEOUT = environment.inactivityTimeout || 3600000;
  private readonly WARNING_TIMEOUT = environment.warningTimeout || 300000;
  /** Idle time before the first “stay signed in?” prompt (full idle-to-logout minus grace period). */
  private readonly INITIAL_TIMEOUT = this.TOTAL_TIMEOUT - this.WARNING_TIMEOUT;
  private readonly idleDurationDisplay = this.formatDurationDisplay(this.INITIAL_TIMEOUT);
  private readonly graceDurationDisplay = this.formatDurationDisplay(this.WARNING_TIMEOUT);

  startTracking(): void {
    if (this.inactivitySubscription) {
      return;
    }

    this.ngZone.runOutsideAngular(() => {
      const activityEvents$ = merge(
        fromEvent(document, 'mousemove'),
        fromEvent(document, 'mousedown'),
        fromEvent(document, 'keydown'),
        fromEvent(document, 'scroll', { capture: true }),
        fromEvent(document, 'touchstart')
      ).pipe(
        startWith(0)
      );

      this.inactivitySubscription = activityEvents$.pipe(
        switchMap(() => {
          this.ngZone.run(() => this.dismissWarningOnActivity());
          return timer(this.INITIAL_TIMEOUT);
        }),
      ).subscribe(() => {
        this.ngZone.run(() => {
          this.showInactivityWarning();
        });
      });
    });
  }

  /** Stop idle timers and start fresh (e.g. after signing in from `/auth/login`). */
  restartTracking(): void {
    this.stopTracking();
    this.startTracking();
  }

  stopTracking(): void {
    if (this.inactivitySubscription) {
      this.inactivitySubscription.unsubscribe();
      this.inactivitySubscription = undefined;
    }
    this.stopWarningTimer();
    this.closeInactivityWarningIfOpen();
  }

  /** User resumed activity while the idle warning was open — cancel forced logout. */
  private dismissWarningOnActivity(): void {
    if (!this.inactivityWarningOpen || !Swal.isVisible()) {
      return;
    }
    this.closeInactivityWarningIfOpen();
    this.stopWarningTimer();
  }

  private closeInactivityWarningIfOpen(): void {
    if (!this.inactivityWarningOpen || !Swal.isVisible()) {
      return;
    }
    this.inactivityWarningOpen = false;
    Swal.close();
  }

  private showInactivityWarning(): void {
    if (this.authService.isMaxSessionAgeExceeded()) {
      this.performLogout();
      return;
    }
    const hasToken = this.checkAuthToken();
    if (!hasToken) return;

    // Start the countdown timer for final logout
    this.startWarningTimer();

    this.inactivityWarningOpen = true;
    this.alertService.fire({
      title: 'Session timeout',
      html: `You have been inactive for about <strong>${this.idleDurationDisplay}</strong>. You will be signed out in <strong>${this.graceDurationDisplay}</strong> unless you stay signed in.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Stay logged in',
      cancelButtonText: 'Sign out',
      confirmButtonColor: '#3b82f6',
      allowOutsideClick: false,
      allowEscapeKey: false,
    }).then((result) => {
      this.inactivityWarningOpen = false;
      if (result.isConfirmed) {
        this.resetSession();
      } else if (result.isDismissed && result.dismiss === Swal.DismissReason.cancel) {
        this.performLogout();
      }
    });
  }

  private startWarningTimer(): void {
    this.stopWarningTimer();
    this.warningTimerSubscription = timer(this.WARNING_TIMEOUT).subscribe(() => {
      this.performLogout();
    });
  }

  private stopWarningTimer(): void {
    if (this.warningTimerSubscription) {
      this.warningTimerSubscription.unsubscribe();
      this.warningTimerSubscription = undefined;
    }
  }

  private resetSession(): void {
    this.stopWarningTimer();

    if (this.authService.isMaxSessionAgeExceeded()) {
      this.performLogout();
      return;
    }

    // Same cookie-based POST /auth/refresh as the auth interceptor — not TokenRefreshService,
    // which expects a localStorage refresh_token this app does not use.
    this.authService.refreshAccessToken().subscribe({
      next: (res) => {
        const t = res.accessToken?.trim();
        if (!t) {
          this.performLogout();
          return;
        }
        this.stopTracking();
        this.startTracking();
      },
      error: () => {
        this.performLogout();
      },
    });
  }

  private performLogout(): void {
    this.stopWarningTimer();
    this.closeInactivityWarningIfOpen();
    this.stopTracking();
    this.authService.logout({ showSessionTimeoutToast: true });
  }

  private checkAuthToken(): boolean {
    return !!(this.storageService.getItem<string>(STORAGE_KEYS.AUTH_TOKEN) || this.storageService.getItem<string>('token'));
  }

  private formatDurationDisplay(ms: number): string {
    const minutes = Math.max(1, Math.round(ms / 60000));
    if (minutes >= 120) {
      const hours = Math.round(minutes / 60);
      return `${hours} hours`;
    }
    if (minutes >= 60) {
      return '1 hour';
    }
    return `${minutes} minutes`;
  }
}

